import { FC } from 'react';
import { Badge, Button, Input, Spinner } from 'reactstrap';
import MustHaveTermsInput from './MustHaveTermsInput';
import { BatchControlCardProps } from './types';
import { TOP_N_OPTIONS } from './utils';

/**
 * Batch Matches control card (README §Batch Matches): meta line + "Brief
 * used" pill, Top-N results select, Refresh, and the must-have terms chip
 * input. AI-explain is always-on for Batch (LLM-score-driven matching), so
 * there's no toggle here anymore. All grading/disposition UI is explicitly
 * out of scope for 2a (STORY-2A-SPEC "What Story 2a EXCLUDES") — this card
 * has none of it.
 */

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const META_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  marginBottom: 14,
};

const BRIEF_PILL_STYLE: React.CSSProperties = {
  background: '#D1FAE5',
  color: '#066E52',
  fontWeight: 600,
  fontSize: 11,
  padding: '3px 9px',
  borderRadius: 999,
};

const CONTROLS_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 16,
  paddingBottom: 16,
  borderBottom: '1px solid #E2E5EA',
};

const formatComputedAt = (computedAt: string | null): string | null => {
  if (!computedAt) return null;
  const date = new Date(computedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const BatchControlCard: FC<BatchControlCardProps> = ({
  topN,
  onTopN,
  mustHave,
  onMustHave,
  briefUsed,
  computedAt,
  onRefresh,
  loading,
}) => {
  const computedLabel = formatComputedAt(computedAt);

  return (
    <div style={CARD_STYLE}>
      <div style={META_ROW_STYLE}>
        <span style={{ fontSize: 13, color: '#667085' }}>
          {computedLabel
            ? `Computed ${computedLabel} · nightly matcher`
            : 'Live match · no nightly batch yet for this job'}
        </span>
        {briefUsed && <Badge style={BRIEF_PILL_STYLE}>Brief used</Badge>}
      </div>

      <div style={CONTROLS_ROW_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#667085', margin: 0 }}>Results</label>
          <Input
            type="select"
            value={topN}
            onChange={(e) => onTopN(Number(e.target.value))}
            style={{ height: 34, width: 100, fontSize: 13 }}
          >
            {TOP_N_OPTIONS.map((n) => (
              <option key={n} value={n}>
                Top {n}
              </option>
            ))}
          </Input>
        </div>

        <Button type="button" color="secondary" outline size="sm" onClick={onRefresh} disabled={loading}>
          {loading && <Spinner size="sm" style={{ marginRight: 6 }} />}
          Search
        </Button>
      </div>

      <MustHaveTermsInput terms={mustHave} onChange={onMustHave} max={16} />
    </div>
  );
};

export default BatchControlCard;
